import {useEffect, useState} from 'react';
import {Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Field, FieldGroup, Label} from '@/components/tailwind/fieldset';
import {Switch} from '@/components/tailwind/switch';
import {Input} from '@/components/tailwind/input';
import {useOrdersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Text} from '@/components/tailwind/text';
import {formatDateString, formatCurrency} from '@/lib/utils';
import {OrderDetails} from '@/models/order';
import {OrderTime} from '@/models/order-time';
import {OrderCostCalculation, getOrderCosts} from '@/services/order-services';
import {getSocialAccountEnabled, getOverdraftAllowed, getSocialAccountBalance} from '@/services/social-account-settings-services';
import {useAuth} from '@/context/use-auth';

interface OrderCompleteDialogProps {
    isOpen: boolean;
    onConfirm: (accountingDate: string, donatePointsToSocialAccount: boolean) => void;
    onClose: () => void;
    orderDetails: OrderDetails;
    orderTimes: OrderTime[];
}

export default function OrderCompleteDialog({
    isOpen,
    onConfirm,
    onClose,
    orderDetails,
    orderTimes
}: OrderCompleteDialogProps) {
    const {t} = useOrdersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();

    const latestActivityDate = orderTimes.length > 0
        ? orderTimes.reduce((max, ot) => (ot.date > max ? ot.date : max), orderTimes[0].date)
        : undefined;

    const orderDate = orderDetails?.orderDate ?? new Date().toISOString().split('T')[0];

    // Mirror the backend rules: accountingDate must be >= orderDate AND >= latestActivityDate.
    const minAccountingDate = latestActivityDate && latestActivityDate > orderDate
        ? latestActivityDate
        : orderDate;

    // Default to the current date, but never below the backend-enforced minimum
    // (the clamp only matters when an activity is booked in the future).
    const today = new Date().toISOString().split('T')[0];
    const defaultAccountingDate = minAccountingDate > today ? minAccountingDate : today;

    const [accountingDate, setAccountingDate] = useState(defaultAccountingDate);
    const [validationKey, setValidationKey] = useState<string | null>(null);
    const [costs, setCosts] = useState<OrderCostCalculation | null>(null);
    const [socialAccountEnabled, setSocialAccountEnabled] = useState(false);
    const [overdraftAllowed, setOverdraftAllowed] = useState(false);
    const [socialAccountBalance, setSocialAccountBalance] = useState(0);
    const [donatePoints, setDonatePoints] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setAccountingDate(defaultAccountingDate);
            setDonatePoints(false);
        }
    }, [isOpen, defaultAccountingDate]);

    useEffect(() => {
        async function loadSocialAccount() {
            if (isOpen && token) {
                try {
                    const [enabled, overdraft, balance] = await Promise.all([
                        getSocialAccountEnabled(token),
                        getOverdraftAllowed(token),
                        getSocialAccountBalance(token),
                    ]);
                    setSocialAccountEnabled(enabled);
                    setOverdraftAllowed(overdraft);
                    setSocialAccountBalance(balance);
                } catch (_error) {
                    setSocialAccountEnabled(false);
                    setOverdraftAllowed(false);
                    setSocialAccountBalance(0);
                }
            }
        }
        loadSocialAccount();
    }, [isOpen, token]);

    useEffect(() => {
        async function loadCosts() {
            if (isOpen && orderDetails?.id && token) {
                try {
                    const costData = await getOrderCosts(token, orderDetails.id);
                    if (costData) {
                        setCosts(costData);
                    }
                } catch (_error) {
                    // Silently fail - costs are optional for display
                }
            }
        }
        loadCosts();
    }, [isOpen, orderDetails?.id, token]);

    function handleConfirm() {
        if (!accountingDate) {
            setValidationKey('completeDialog.validation.required');
            return;
        }
        if (accountingDate < minAccountingDate) {
            const isBoundByActivity = latestActivityDate != null && latestActivityDate > orderDate;
            setValidationKey(isBoundByActivity
                ? 'completeDialog.validation.beforeLatestActivity'
                : 'completeDialog.validation.beforeOrderDate');
            return;
        }
        if (socialPaymentBlocked) return;
        onConfirm(accountingDate, donatePoints);
    }

    function handleClose() {
        setValidationKey(null);
        onClose();
    }

    const hasReimbursements = costs && (costs.mileage > 0 || costs.reimbursableAmount > 0);
    const hasDonation = orderDetails.donationAmount != null && orderDetails.donationAmount > 0;
    const canDonatePoints = socialAccountEnabled
        && orderDetails.helper != null
        && costs != null
        && costs.activityPoints > 0;
    // A concurrent donation credits the same amount, so it covers the payment too.
    const socialPaymentCovered = overdraftAllowed
        || donatePoints
        || (costs != null && costs.activityPoints <= socialAccountBalance);
    const socialPaymentBlocked = orderDetails.paidFromSocialAccount
        && costs != null
        && costs.activityPoints > 0
        && !socialPaymentCovered;

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <DialogTitle data-testid="complete-dialog-title">{t("completeDialog.title")}</DialogTitle>
            <DialogDescription>
                {t("completeDialog.description")}
            </DialogDescription>
            <DialogBody>
                <FieldGroup>
                    <Field>
                        <Label htmlFor="accounting-date">{t("completeDialog.accountingDateLabel")}</Label>
                        <Input
                            id="accounting-date"
                            type="date"
                            value={accountingDate}
                            onChange={(e) => {
                                setAccountingDate(e.target.value);
                                setValidationKey(null);
                            }}
                            max={today}
                            invalid={!!validationKey}
                            data-testid="accounting-date-picker"
                        />
                        {validationKey && (
                            <p className="text-red-600 text-sm mt-1" data-testid="accounting-date-error">
                                {t(validationKey, {date: formatDateString(minAccountingDate)})}
                            </p>
                        )}
                        <Text className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                            {t("completeDialog.orderDate", {date: formatDateString(orderDetails?.orderDate || '')})}
                        </Text>
                    </Field>
                </FieldGroup>

                {costs && (
                    <div className="mt-6 space-y-4">
                        <div className="border-t pt-4">
                            <Text className="font-semibold text-lg mb-4" data-testid="costs-overview-title">
                                {t("completeDialog.costs.title")}
                            </Text>

                            {/* Group: Helfer */}
                            <div className="mb-4">
                                <div className="border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">
                                    <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                        {t("orderDetails.sections.costs.helperGroup")}
                                    </Text>
                                </div>
                                <div className="space-y-1 text-sm">
                                    {costs.activityPoints > 0 && (
                                        <div className="flex justify-between px-2 py-0.5">
                                            <Text className="text-gray-600 dark:text-gray-400">
                                                {t("completeDialog.costs.servicePoints")}
                                            </Text>
                                            <Text className="font-medium" data-testid="service-points-value">
                                                {donatePoints
                                                    ? t("completeDialog.costs.servicePointsDonated", {points: costs.activityPoints})
                                                    : t("completeDialog.costs.servicePointsValue", {points: costs.activityPoints})}
                                            </Text>
                                        </div>
                                    )}
                                    {canDonatePoints && (
                                        <div className="flex items-center justify-between px-2 py-1">
                                            <Text className="text-gray-600 dark:text-gray-400">
                                                {t("completeDialog.donation.label")}
                                            </Text>
                                            <Switch
                                                checked={donatePoints}
                                                onChange={setDonatePoints}
                                                color="blue"
                                                aria-label={t("completeDialog.donation.label")}
                                                data-testid="donate-points-switch"
                                            />
                                        </div>
                                    )}
                                    {costs.mileage > 0 && (
                                        <div className="flex justify-between px-2 py-0.5">
                                            <Text className="text-gray-600 dark:text-gray-400">
                                                {t("orderDetails.sections.costs.mileage")}: {costs.mileage} km
                                            </Text>
                                            <Text className="font-medium">{formatCurrency(costs.mileageCost)}</Text>
                                        </div>
                                    )}
                                    {costs.reimbursableAmount > 0 && (
                                        <div className="flex justify-between px-2 py-0.5">
                                            <Text className="text-gray-600 dark:text-gray-400">
                                                {t("orderDetails.sections.costs.reimbursableAmount")}
                                            </Text>
                                            <Text className="font-medium">{formatCurrency(costs.reimbursableAmount)}</Text>
                                        </div>
                                    )}
                                    {costs.activityPoints === 0 && !hasReimbursements && (
                                        <div className="px-2 py-0.5 text-gray-500 dark:text-gray-500 italic">-</div>
                                    )}
                                </div>
                            </div>

                            {/* Group: Verwaltung */}
                            <div>
                                <div className="border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">
                                    <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                        {t("orderDetails.sections.costs.adminGroup")}
                                    </Text>
                                </div>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between px-2 py-0.5" data-testid={orderDetails.payWithPoints ? "payment-method-points" : "payment-method-office"}>
                                        <Text className="text-gray-600 dark:text-gray-400">
                                            {t("orderDetails.sections.costs.activityFees")}
                                        </Text>
                                        <Text className="font-medium" data-testid="office-amount-value">
                                            {orderDetails.payWithPoints
                                                ? t("orderDetails.sections.costs.activityFeeInPoints", {points: costs.activityPoints})
                                                : formatCurrency(costs.activityFees)}
                                        </Text>
                                    </div>
                                    {hasDonation && (
                                        <div className="flex justify-between px-2 py-0.5">
                                            <Text className="text-gray-600 dark:text-gray-400">
                                                {t("orderDetails.sections.costs.donationAmount")}
                                            </Text>
                                            <Text className="font-medium" data-testid="donation-amount-value">
                                                {formatCurrency(orderDetails.donationAmount!)}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {socialPaymentBlocked && (
                            <p
                                className="text-amber-600 dark:text-amber-500 text-sm"
                                data-testid="social-payment-blocked-hint"
                            >
                                {t("completeDialog.socialPaymentBlocked")}
                            </p>
                        )}
                    </div>
                )}
            </DialogBody>
            <DialogActions>
                <Button
                    plain
                    onClick={handleClose}
                    aria-label={tCommon("buttons.cancel.ariaLabel")}
                    data-testid="complete-dialog-cancel"
                >
                    {tCommon("buttons.cancel.text")}
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={socialPaymentBlocked}
                    aria-label={t("completeDialog.confirm.ariaLabel")}
                    data-testid="complete-dialog-confirm"
                >
                    {t("completeDialog.confirm.text")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
